import React, { useEffect, useState } from 'react';
import { api, EarningsDate } from '../api';

const CACHE_KEY = 'earnings_countdown_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function getCached(): EarningsDate[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data as EarningsDate[];
  } catch { return null; }
}

function setCache(data: EarningsDate[]) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

const EarningsCountdown: React.FC = () => {
  const [earnings, setEarnings] = useState<EarningsDate[]>(() => getCached() ?? []);
  const [loading, setLoading] = useState(() => getCached() === null);

  useEffect(() => {
    const cached = getCached();
    if (cached) {
      setEarnings(cached);
      setLoading(false);
    }
    api.getEarnings()
      .then(data => {
        const sorted = data
          .filter(e => e.daysUntil !== null)
          .sort((a, b) => (a.daysUntil ?? 999) - (b.daysUntil ?? 999));
        setEarnings(sorted);
        setCache(sorted);
      })
      .catch(() => { if (!cached) setEarnings([]); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="earnings-countdown"><div className="ec-loading">Loading...</div></div>;
  if (earnings.length === 0) return null;

  return (
    <div className="earnings-countdown">
      <h4>Upcoming Reports</h4>
      {earnings.map(e => (
        <div key={e.symbol} className="ec-item">
          <div className="ec-row">
            <span className="ec-symbol">{e.symbol}</span>
            <span className={`ec-badge ${e.reportType}`}>
              {e.reportType === 'annual' ? '10-K' : 'Q' + e.quarter}
            </span>
          </div>
          <div className="ec-row">
            <span className="ec-date">{e.date}</span>
            <span className={`ec-days ${e.daysUntil! <= 7 ? 'soon' : e.daysUntil! <= 30 ? 'near' : ''}`}>
              {e.daysUntil === 0 ? 'Today' : `${e.daysUntil}d`}
            </span>
          </div>
          {e.lastClosePrice != null && (
            <div className="ec-row" style={{ marginTop: 2 }}>
              <span className="ec-price" style={{ fontSize: 12, color: '#8b8fa3' }}>Current Price</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: e.lastPercentChange != null ? (e.lastPercentChange >= 0 ? '#22c55e' : '#ef4444') : '#e1e4eb' }}>
                ${e.lastClosePrice.toFixed(2)}
                {e.lastPercentChange != null && (
                  <span style={{ marginLeft: 4, fontSize: 11 }}>
                    ({e.lastPercentChange >= 0 ? '+' : ''}{e.lastPercentChange.toFixed(2)}%)
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default EarningsCountdown;
